<script setup lang="ts">
import { computed, toRefs } from "vue";
import PropertyView from "@/shared/components/views/property-view.vue";
import ListCardCollapsible from "@/shared/components/card/list-card-collapsible.vue";
import { DocumentationGroupResult, GroupedDocumentationItem } from "@/shared/documentation/documentation.types.ts";
import { SearchContext } from "@/shared/search/search.types.ts";
import { CollectionVisibilityTypes } from "@/abilities/collections/models/collections.models.ts";
import { PropertyItem } from "@/shared/components/views/property-item.model.ts";
import { useDocumentRoute } from "@/shared/router/document-route.ts";
import AddToCollectionButton from "@/shared/collections/add-to-collection-button.vue";
import { getDocumentIcon } from "@/shared/documentation/icon.utils.ts";
import { generateTotalText } from "@/shared/utils/total-formatter.ts";
import InfinityScroll from "@/shared/components/scroll/infinity-scroll.vue";

export interface DocumentGroupedListCardProps {
    groupResult: DocumentationGroupResult;
    index?: number;
    context?: SearchContext;
    collectionContext?: {
        user: { id: string };
        visibility: CollectionVisibilityTypes;
    };
    /**
     * Incremented by the parent whenever the loaded items are replaced (e.g. a changed sorting), so that
     * the item list scrolls back to the top and an infinite scroll that already stopped starts over.
     */
    itemsResetKey?: number;
}

const props = defineProps<DocumentGroupedListCardProps>();

/** The item list starts scrolling once it exceeds this height, below it the card only takes the space it needs. */
const MAX_ITEMS_HEIGHT = 200;

const emits = defineEmits<{
    (e: "toggleCollectionOverlay", topicId: string, contentMapId: string): void;
    (e: "loadMoreItems", groupCursor: string, done: (stop?: boolean) => void): void;
}>();

const { groupResult } = toRefs(props);

const { getPathToDocumentationDetailPageForSearch } = useDocumentRoute();

const hasMoreItems = computed(() => groupResult.value.items.length < groupResult.value.group.itemsTotal.count);

const rootPropertyItems = computed<PropertyItem[]>(() => {
    if (groupResult.value.group.mimeType) {
        return [
            {
                key: "Format",
                title: "Format",
                value: groupResult.value.group.mimeType,
            },
        ];
    }
    return [];
});

const linkToDocumentationDetailPage = (document: GroupedDocumentationItem) => {
    return getPathToDocumentationDetailPageForSearch(document.contentMapId, document.topicId, {});
};

const toggleCollectionOverlay = (item: GroupedDocumentationItem) => {
    emits("toggleCollectionOverlay", item.topicId ?? "", item.contentMapId ?? "");
};

const onLoadMoreItems = (done: (stop?: boolean) => void) => {
    if (!hasMoreItems.value) {
        done(true);
        return;
    }

    emits("loadMoreItems", groupResult.value.cursor, done);
};
</script>

<template>
    <ListCardCollapsible
        :title="groupResult.group.title"
        :locale-tag="groupResult.group.language"
        :hits-count="generateTotalText(groupResult.group.itemsTotal) ?? ''"
        :index="index"
        :property-items="rootPropertyItems"
        :test-id="`documentation-group-${index}`"
    >
        <template #content="{ isExpanded }">
            <div v-if="isExpanded" data-qs="group-items">
                <InfinityScroll
                    :key="itemsResetKey"
                    :max-height="MAX_ITEMS_HEIGHT"
                    :reset-key="itemsResetKey"
                    @load-more="onLoadMoreItems"
                >
                    <router-link
                        :to="linkToDocumentationDetailPage(item)"
                        v-for="(item, itemIndex) in groupResult.items"
                        :key="item.cursor"
                        :data-qs="`documentation-group-item-${itemIndex}`"
                        class="row no-wrap flex-grow border-top padding-ml"
                    >
                        <div>
                            <q-icon size="20px" class="q-pr-md fa-light" :class="getDocumentIcon(item.type)" />
                        </div>
                        <div class="row properties flex-grow">
                            <PropertyView :items="item.propertyItems" :max-property-width="180" />
                        </div>
                        <div>
                            <AddToCollectionButton
                                color="secondary"
                                @click="
                                    () => {
                                        toggleCollectionOverlay(item);
                                    }
                                "
                            />
                        </div>
                    </router-link>
                </InfinityScroll>
            </div>
        </template>
    </ListCardCollapsible>
</template>

<style lang="scss" scoped>
.properties {
    column-gap: $spacing-xxl;
    margin-left: $spacing-ml;
}
:deep(.property) {
    padding: 0;
}
</style>
